import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import logo from '../assets/GMF logo no background.png';
import styles from '../Styles/Auth.module.css';

function CreateAccount() {
    const [step, setStep] = useState(1); // Step 1: Basic Info, Step 2: Security Questions
    const [userType, setUserType] = useState(''); // Both buttons start deselected
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [institutionName, setInstitutionName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [securityQuestionOptions, setSecurityQuestionOptions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(true);
    const [securityQuestionsPicked, setSecurityQuestionsPicked] = useState({
        question1: '',
        question2: '',
        question3: '',
    });
    const [securityAnswers, setSecurityAnswers] = useState({
        answer1: '',
        answer2: '',
        answer3: '',
    });

    const [errors, setErrors] = useState({});
    const [passwordRequirements, setPasswordRequirements] = useState({
        minLength: false,
        hasSpecialChar: false,
    });
    const [passwordTouched, setPasswordTouched] = useState(false); // Track if the password field has been touched

    // confirm password validation
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    // Fetch security questions from the API so they aren't hardcoded here
    useEffect(() => {
        const fetchSecurityQuestions = async () => {
            try {
                // need to make sure to have a slash at the end
                const response = await fetch(`${API_URL}/security-questions/`);
                if (response.ok) {
                    const data = await response.json();
                    setSecurityQuestionOptions(data.security_questions);
                } else {
                    console.error('Failed to fetch security questions');
                }
            } catch (error) {
                console.error('Error fetching security questions:', error);
            } finally {
                setLoadingQuestions(false);
            }
        };

        fetchSecurityQuestions();
    }, [API_URL]);


    const validatePassword = (password) => {
        const requirements = {
            minLength: password.length >= 12,
            hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        };
        setPasswordRequirements(requirements);
        return Object.values(requirements).every(Boolean);
    };

    // original password
    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        validatePassword(value);
    };

    const handlePasswordBlur = () => {
        setPasswordTouched(true); // Mark the password field as touched
    };

    const handleBlur = (field, value) => {
        setErrors((prev) => ({
            ...prev,
            [field]: !Boolean(value),
        }));
    
        if (field === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            setErrors((prev) => ({
                ...prev,
                email: !emailRegex.test(value),
            }));
        }
    
        if (field === 'confirmPassword') {
            setConfirmPasswordTouched(true); // Mark the confirm password field as touched
            setErrors((prev) => ({
                ...prev,
                confirmPassword: value !== password, // Check if passwords match
            }));
        }
    };

    const handleChange = (field, value) => {
        if (field === 'firstName') {
            setFirstName(value);
            setErrors((prev) => ({
                ...prev,
                firstName: !Boolean(value),
            }));
        }

        if (field === 'lastName') {
            setLastName(value);
            setErrors((prev) => ({
                ...prev,
                lastName: !Boolean(value),
            }));
        }

        if (field === 'email') {
            setEmail(value);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            setErrors((prev) => ({
                ...prev,
                email: !emailRegex.test(value),
            }));
        }

        if (field.startsWith('answer')) {
            const index = field.replace('answer', '');
            setSecurityAnswers((prev) => ({
                ...prev,
                [`answer${index}`]: value,
            }));
            setErrors((prev) => ({
                ...prev,
                [`answer${index}`]: !Boolean(value),
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validatePassword(password) || confirmPassword !== password) {
            setErrors((prev) => ({
                ...prev,
                password: !validatePassword(password),
                confirmPassword: confirmPassword !== password,
            }));
            return;
        }

        try {
            // Dynamically set the endpoint and payload based on userType
            const endpoint =
                userType === 'GMF' ? '/gmf-employee/' : '/institution-account/';

            const payload =
                userType === 'GMF'
                    ? {
                          first_name: firstName,
                          last_name: lastName,
                          email,
                          password,
                          security_question_1: securityQuestionsPicked.question1,
                          security_answer_1: securityAnswers.answer1,
                          security_question_2: securityQuestionsPicked.question2,
                          security_answer_2: securityAnswers.answer2,
                          security_question_3: securityQuestionsPicked.question3,
                          security_answer_3: securityAnswers.answer3,
                      }
                    : {
                          institution_name: institutionName, // Use the new institutionName state
                          email,
                          password,
                          security_question_1: securityQuestionsPicked.question1,
                          security_answer_1: securityAnswers.answer1,
                          security_question_2: securityQuestionsPicked.question2,
                          security_answer_2: securityAnswers.answer2,
                          security_question_3: securityQuestionsPicked.question3,
                          security_answer_3: securityAnswers.answer3,
                      };

            const response = await fetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                alert(
                    'Account created successfully! NOTE: Your account will need to be approved by Global Medic Force before you can log in.'
                );
                navigate('/');
            } else {
                const data = await response.json();
                setErrors((prev) => ({
                    ...prev,
                    form: data.message || 'Failed to create account.',
                }));
            }
        } catch (err) {
            setErrors((prev) => ({
                ...prev,
                form: 'An error occurred. Please try again.',
            }));
        }
    };


    return (
        <div className={styles.home}>
            <header className={styles.headerSection}>
                <div className={styles.contentContainer}>
                    {/* <img
                        className={styles.logo}
                        src={logo}
                        alt="MD-Sensei Logo"
                    /> */}
                    <h1 className={styles.title}>Create Account</h1>

                    <form onSubmit={handleSubmit} noValidate>
                        {/* Step 1: Basic Information */}
                        {step === 1 && (
                            <>
                                {/* header text and instructions */}
                                <p>
                                    Welcome! Create an account for the Admin
                                    Platform.
                                </p>
                                <p>
                                    Select "Global Medic Force" if you volunteer
                                    for GMF directly, or "Institution" if you
                                    represent an organization (e.g., Ministry of
                                    Health).
                                    <strong> Note:</strong> Each Institution is
                                    only associated with one account.
                                </p>
                                <p>
                                    Field healthcare workers, please use the
                                    mobile app.
                                </p>
                                {/* Role Selection */}
                                <div className={styles.formGroup}>
                                    <label>Select Your Role:</label>
                                    <div className={styles.roleSelection}>
                                        <button
                                            type="button"
                                            className={`${styles.roleButton} ${
                                                userType === 'GMF'
                                                    ? styles.selected
                                                    : styles.unselectedButton
                                            }`}
                                            onClick={() => {
                                                setUserType('GMF');
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    userType: false, // Clear the error when a role is selected
                                                }));
                                            }}
                                        >
                                            Global Medic Force
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.roleButton} ${
                                                userType === 'institution'
                                                    ? styles.selected
                                                    : styles.unselectedButton
                                            }`}
                                            onClick={() => {
                                                setUserType('institution');
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    userType: false, // Clear the error when a role is selected
                                                }));
                                            }}
                                        >
                                            Institution
                                        </button>
                                    </div>
                                    {errors.userType && (
                                        <p className={styles.errorMessage}>Please select a role.</p>
                                    )}
                                </div>

                                {/* First Name and Last Name for GMF */}
                                {userType === 'GMF' && (
                                    <>
                                        <div className={styles.formGroup}>
                                            <label>First Name:</label>
                                            <input
                                                type="text"
                                                value={firstName}
                                                onChange={(e) =>
                                                    handleChange(
                                                        'firstName',
                                                        e.target.value
                                                    )
                                                }
                                                onBlur={() =>
                                                    handleBlur(
                                                        'firstName',
                                                        firstName
                                                    )
                                                }
                                                required
                                            />
                                            {errors.firstName && (
                                                <p
                                                    className={
                                                        styles.errorMessage
                                                    }
                                                >
                                                    First name is required.
                                                </p>
                                            )}
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Last Name:</label>
                                            <input
                                                type="text"
                                                value={lastName}
                                                onChange={(e) =>
                                                    handleChange(
                                                        'lastName',
                                                        e.target.value
                                                    )
                                                }
                                                onBlur={() =>
                                                    handleBlur(
                                                        'lastName',
                                                        lastName
                                                    )
                                                }
                                                required
                                            />
                                            {errors.lastName && (
                                                <p
                                                    className={
                                                        styles.errorMessage
                                                    }
                                                >
                                                    Last name is required.
                                                </p>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Institution Name Field */}
                                {userType === 'institution' && (
                                    <div className={styles.formGroup}>
                                        <label>Institution Name:</label>
                                        <input
                                            type="text"
                                            value={institutionName}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setInstitutionName(value);
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    institutionName:
                                                        !value.trim(),
                                                }));
                                            }}
                                            required
                                        />
                                        {errors.institutionName && (
                                            <p className={styles.errorMessage}>
                                                Institution name is required.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Email */}
                                <div className={styles.formGroup}>
                                    <label>Email:</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) =>
                                            handleChange(
                                                'email',
                                                e.target.value
                                            )
                                        }
                                        onBlur={() =>
                                            handleBlur('email', email)
                                        }
                                        required
                                    />
                                    {errors.email && (
                                        <p className={styles.errorMessage}>
                                            Please enter a valid email address.
                                        </p>
                                    )}
                                </div>

                                {/* Password */}
                                <div className={styles.formGroup}>
                                    <label>Password:</label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        onBlur={handlePasswordBlur}
                                        required
                                    />
                                    <ul className={styles.passwordRequirements}>
                                        <li
                                            className={
                                                passwordRequirements.minLength
                                                    ? styles.valid
                                                    : passwordTouched
                                                    ? styles.invalid
                                                    : styles.defaultText
                                            }
                                        >
                                            At least 12 characters
                                        </li>
                                        <li
                                            className={
                                                passwordRequirements.hasSpecialChar
                                                    ? styles.valid
                                                    : passwordTouched
                                                    ? styles.invalid
                                                    : styles.defaultText
                                            }
                                        >
                                            At least one special character (one of the following: !@#$%^&*(),.?":{}|<></>) - don't be predictable and put it at the end!
                                        </li>
                                    </ul>
                                </div>

                                {/* Confirm Password */}
                                <div className={styles.formGroup}>
                                    <label>Confirm Password:</label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setConfirmPassword(value);
                                            // Validate in real-time but don't show the error yet
                                            setErrors((prev) => ({
                                                ...prev,
                                                confirmPassword: value !== password,
                                            }));
                                        }}
                                        onBlur={() => {
                                            setConfirmPasswordTouched(true); // Mark the field as touched
                                        }}
                                        required
                                    />
                                    {confirmPasswordTouched && errors.confirmPassword && (
                                        <p className={styles.errorMessage}>Passwords do not match.</p>
                                    )}
                                </div>

                                {/* Next Button */}
                                <button
                                    type="button"
                                    className={styles.submitButton}
                                    onClick={() => {
                                        if (!userType) {
                                            setErrors((prev) => ({
                                                ...prev,
                                                userType: true, // Set error if no role is selected
                                            }));
                                            return;
                                        }
                                        setStep(2); // Move to Step 2 if validation passes
                                    }}
                                    disabled={
                                        !passwordRequirements.minLength ||
                                        !passwordRequirements.hasSpecialChar ||
                                        confirmPassword !== password ||
                                        !email ||
                                        errors.email || // Ensure email is valid
                                        (userType === 'GMF' && (!firstName || !lastName)) ||
                                        (userType === 'institution' && !institutionName.trim()) ||
                                        userType === ''
                                    }
                                >
                                    Next
                                </button>
                            </>
                        )}

                        {/* Step 2: Security Questions */}
                        {step === 2 && (
                            <>
                                <h3>You're Almost There! 🎉</h3>
                                <p className={styles.instructions}>
                                    Just one more step! Please answer the
                                    security questions below. These will help us
                                    verify your identity if you ever need to
                                    reset your password. 😊
                                </p>

                                <p className={styles.instructions}>
                                    Note: Answers are <em>not</em> case-sensitive to make your life a little easier, while remaining secure 😊
                                </p>

                                {['question1', 'question2', 'question3'].map(
                                    (question, index) => (
                                        <div
                                            className={styles.formGroup}
                                            key={question}
                                        >
                                            <label>
                                                Security Question {index + 1}:
                                            </label>
                                            <select
                                                className={styles.input}
                                                value={
                                                    securityQuestionsPicked[question]
                                                }
                                                onChange={(e) =>
                                                    setSecurityQuestionsPicked(
                                                        (prev) => ({
                                                            ...prev,
                                                            [question]:
                                                                e.target.value,
                                                        })
                                                    )
                                                }
                                                required
                                            >
                                                <option value="">
                                                    Select a question
                                                </option>
                                                {securityQuestionOptions
                                                    .filter(
                                                        (option) =>
                                                            !Object.values(
                                                                securityQuestionsPicked
                                                            ).includes(
                                                                option
                                                            ) ||
                                                            option ===
                                                                securityQuestionsPicked[
                                                                    question
                                                                ]
                                                    )
                                                    .map((option, idx) => (
                                                        <option
                                                            key={idx}
                                                            value={option}
                                                        >
                                                            {option}
                                                        </option>
                                                    ))}
                                            </select>
                                            {errors[question] && (
                                                <p
                                                    className={
                                                        styles.errorMessage
                                                    }
                                                >
                                                    Please select a question.
                                                </p>
                                            )}

                                            <input
                                                type="text"
                                                placeholder="Your Answer"
                                                value={
                                                    securityAnswers[
                                                        `answer${index + 1}`
                                                    ]
                                                }
                                                onChange={(e) => {
                                                    const value =
                                                        e.target.value;
                                                    setSecurityAnswers(
                                                        (prev) => ({
                                                            ...prev,
                                                            [`answer${
                                                                index + 1
                                                            }`]: value,
                                                        })
                                                    );
                                                    setErrors((prev) => ({
                                                        ...prev,
                                                        [`answer${index + 1}`]:
                                                            !Boolean(value),
                                                    }));
                                                }}
                                                onBlur={(e) => {
                                                    const value =
                                                        e.target.value;
                                                    setErrors((prev) => ({
                                                        ...prev,
                                                        [`answer${index + 1}`]:
                                                            !Boolean(value),
                                                    }));
                                                }}
                                                required
                                            />
                                            {errors[`answer${index + 1}`] && (
                                                <p
                                                    className={
                                                        styles.errorMessage
                                                    }
                                                >
                                                    This answer is required.
                                                </p>
                                            )}
                                        </div>
                                    )
                                )}

                                {/* Back and Submit Buttons */}
                                <div>
                                    <button
                                        type="button"
                                        className={styles.backButton}
                                        onClick={() => setStep(1)} // Go back to Step 1
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.submitButton}
                                        disabled={
                                            Object.values(
                                                securityQuestionsPicked
                                            ).some((q) => !q) ||
                                            Object.values(securityAnswers).some(
                                                (a) => !a
                                            )
                                        }
                                    >
                                        Create Account
                                    </button>
                                </div>
                            </>
                        )}
                    </form>

                    {/* Link to Login Page */}
                    <p className={styles.linkText}>
                        Already have an account? <Link to="/login">Login</Link>.
                    </p>
                </div>
            </header>
        </div>
    );
}

export default CreateAccount;
